import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { HttpErrorResponse } from '@angular/common/http';
import { Subject, Subscription, firstValueFrom, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';
import { StammkundeService } from '../../../Services/stammkunde.service';
import { CaptureQueueService } from '../../../Services/capture-queue.service';
import {
  AuftragAssignment,
  TAKEDA_REFERENZ_TYP_LABELS,
  TAKEDA_TAGESBERICHT_STATUS_LABELS,
  TakedaArbeitsbereich,
  TakedaReferenzTyp,
  TakedaTagesbericht,
  TakedaTagesberichtSave,
  TakedaArbeitsbereichLvPosition,
  TakedaLeistungsverzeichnis,
  TakedaEinsatzPlanLvPosition,
  TakedaTagesberichtLvPosition,
  TakedaTagesberichtStatus
} from '../../../Models/stammkunde.model';
import { ZeitPerson } from '../zeit-eintrag-form/zeit-eintrag-form.component';
import { ConfirmDialogComponent } from '../../shared/confirm-dialog/confirm-dialog.component';
import { istEinsatztag } from '../shared/einsatz-tage.util';

export interface TakedaTagesberichtData {
  assignment: AuftragAssignment;
  /** Tag des Berichts (yyyy-MM-dd). */
  workDate: string;
  /** Eingeplante Kolonne dieses Einsatzes (Vorbelegung neuer Arbeitsbereiche). */
  plannedPeople: ZeitPerson[];
  /** Vollständige Auswahl: Team-Mitglieder + Arbeiter des Stammkunden. */
  rosterPeople: ZeitPerson[];
}

/** Ergebnis beim Schließen: submitted = Stunden wurden als Zeiteinträge übernommen. */
export interface TakedaTagesberichtResult {
  saved: boolean;
  submitted: boolean;
}

interface PersonRow {
  key: string;
  label: string;
  personUserId?: string | null;
  workerId?: number | null;
  personName?: string | null;
  hours: number | null;
}

/**
 * Im Arbeitsbereich verwendete LV-Position.
 *
 * Bewusst OHNE Preisfelder: der Server liefert sie im Tagesbericht gar nicht aus,
 * und gespeichert werden ohnehin nur `lvPositionId` + `menge` – den Snapshot samt
 * Preisen erzeugt das Backend selbst aus der echten LV-Position.
 */
interface LvRow {
  /** ID des gespeicherten Snapshot-Eintrags im Tagesbericht. */
  id?: number | null;

  /** ID der ursprünglichen TakedaLvPosition. */
  lvPositionId?: number | null;

  nummer: string;
  stichwort?: string | null;
  bezeichnung?: string | null;
  typ?: string | null;
  einheit?: string | null;

  menge: number | null;
}

/**
 * Frei erfasstes Material ohne LV-Bezug.
 *
 * `id` wird für bereits gespeicherte Zeilen mitgeführt und beim Speichern
 * zurückgesendet, damit das Backend einen dort schon gepflegten Preis erhalten
 * kann. Einen Preis kennt bzw. sendet der Tagesbericht selbst nie.
 */
interface FreiesMaterialRow {
  id?: number | null;
  bezeichnung: string;
  menge: number | null;
  einheit: string;
}

interface BereichForm {
  referenzTyp: TakedaReferenzTyp;
  referenzNummer: string;
  ort: string;
  beschreibung: string;
  materialText: string;

  personen: PersonRow[];

  /** Im Arbeitsbereich verwendete LV-Positionen. */
  lvPositionen: LvRow[];

  /** Frei erfasstes Material dieses Arbeitsbereichs. */
  freieMaterialien: FreiesMaterialRow[];

  /** Suchtext für das Leistungsverzeichnis. */
  lvSearch: string;

  /**
   * Eingabestrom der Live-Suche – je Arbeitsbereich ein eigener, damit sich
   * mehrere Bereiche nicht gegenseitig überschreiben.
   */
  lvSearch$: Subject<string>;

  /** Zugehörige Subscription; wird beim Entfernen des Bereichs beendet. */
  lvSearchSub: Subscription | null;

  /** Aktuelle Suchergebnisse. */
  lvSearchResults: TakedaTagesberichtLvPosition[];

  lvSearchLoading: boolean;

  /** Erst nach einer echten Suche „keine Treffer" anzeigen. */
  lvSearchTouched: boolean;

  /** Auswahl im "weitere Person"-Feld. */
  addKey: string;
}

/**
 * Digitaler Tagesbericht für Takeda-Einsätze: mehrere Arbeitsbereiche (SES/Angebot)
 * mit Beschreibung, Material-Freitext und Personenstunden. Als Entwurf speicherbar;
 * beim Abschließen legt das Backend die normalen Stunden-Einträge an, danach ist
 * der Bericht schreibgeschützt. Nur online nutzbar.
 */
@Component({
  selector: 'app-takeda-tagesbericht',
  templateUrl: './takeda-tagesbericht.component.html',
  styleUrls: ['./takeda-tagesbericht.component.css', '../stammkunden-theme.css']
})
export class TakedaTagesberichtComponent implements OnInit, OnDestroy {
  readonly referenzTypen: TakedaReferenzTyp[] = ['Ses', 'Angebot'];
  readonly referenzTypLabels = TAKEDA_REFERENZ_TYP_LABELS;
  readonly maxReferenzLength = 100;

  /** Live-Suche: erst ab 2 Zeichen und mit 300 ms Verzögerung. */
  readonly minLvSearchLength = 2;
  readonly lvSearchDebounceMs = 300;
  readonly maxFreiesMaterialBezeichnungLength = 256;

  workDate = '';
  status: TakedaTagesberichtStatus = 'Draft';
  report: TakedaTagesbericht | null = null;
  bereiche: BereichForm[] = [];

  loading = false;
  loadError: string | null = null;
  saving = false;
  showErrors = false;

  activeLv: TakedaLeistungsverzeichnis | null = null;
  activeLvLoading = false;

  private savedOnce = false;
  private submitted = false;
  /** Stand beim Laden/Speichern – Grundlage für die Warnung vor ungespeicherten Änderungen. */
  private cleanSnapshot = '';
  private subs = new Subscription();



  constructor(
    public dialogRef: MatDialogRef<TakedaTagesberichtComponent, TakedaTagesberichtResult>,
    @Inject(MAT_DIALOG_DATA) public data: TakedaTagesberichtData,
    private stammkundeService: StammkundeService,
    private captureQueue: CaptureQueueService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) { }

  ngOnInit(): void {
    this.workDate = this.data.workDate;

    this.subs.add(this.dialogRef.backdropClick().subscribe(() => this.close()));
    this.subs.add(this.dialogRef.keydownEvents()
      .pipe(filter(ev => ev.key === 'Escape'))
      .subscribe(() => this.close()));

    this.loadActiveLv();
    this.load();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    // Suchströme aller Arbeitsbereiche beenden.
    this.bereiche.forEach(b => this.detachLvSearch(b));
  }

  // ── Anzeige ──

  get assignment(): AuftragAssignment {
    return this.data.assignment;
  }

  get readOnly(): boolean {
    return this.status !== 'Draft' || !!this.loadError;
  }

  get statusLabel(): string {
    return this.report ? TAKEDA_TAGESBERICHT_STATUS_LABELS[this.status] ?? this.status : 'Neu';
  }

  get isOffline(): boolean {
    return !this.captureQueue.isOnline;
  }

  /** Abschlusszeitpunkt (Backend liefert UTC ohne Zonenkennung). */
  get submittedAt(): Date | null {
    const value = this.report?.submittedAtUtc;
    if (!value) { return null; }
    return new Date(/([zZ]|[+-]\d{2}:\d{2})$/.test(value) ? value : `${value}Z`);
  }

  /** Mehrtägiger Einsatz: Tag des Berichts wählbar (innerhalb des Einsatzzeitraums). */
  get isMultiDay(): boolean {
    return this.minDate !== this.maxDate;
  }

  // ── Datum ──
  //
  // Maßgeblich bleibt `workDate` als 'yyyy-MM-dd'-String: daran hängen Laden,
  // Payload (`${workDate}T00:00:00`, bewusst OHNE UTC-Umrechnung) und die
  // Grenzen des Einsatzzeitraums. Der Material-Datepicker arbeitet mit
  // Date-Objekten und bekommt deshalb nur einen Zugang davor – so bleibt das
  // Backend-Format und die bestehende Validierung unangetastet.

  get workDateValue(): Date | null {
    return this.vonDateInput(this.workDate);
  }

  set workDateValue(value: Date | null) {
    if (!value) { return; }

    const neu = this.toDateInput(value);
    if (neu === this.workDate) { return; }

    this.workDate = neu;
    // Gleicher Weg wie beim bisherigen Eingabefeld: Rückfrage bei
    // ungespeicherten Änderungen, dann neu laden.
    void this.onDateChange();
  }

  /** Nur geplante Einsatztage wählbar – Sa/So nur, wenn ausdrücklich eingeplant. */
  readonly einsatztagFilter = (tag: Date | null): boolean =>
    !tag || istEinsatztag(this.assignment, tag);

  get minDateValue(): Date | null {
    return this.vonDateInput(this.minDate);
  }

  get maxDateValue(): Date | null {
    return this.vonDateInput(this.maxDate);
  }

  get minDate(): string {
    return this.toDateInput(new Date(this.assignment.start));
  }

  get maxDate(): string {
    return this.toDateInput(new Date(this.assignment.end));
  }

  get totalHours(): number {
    return this.bereiche.reduce((sum, b) => sum + this.bereichHours(b), 0);
  }

  bereichHours(b: BereichForm): number {
    return b.personen.reduce((sum, p) => sum + (Number(p.hours) || 0), 0);
  }

  /**
   * SES-/IND-Nummer des Projekts. Kommt aus dem Auftrag, nicht aus dem Bericht:
   * vor dem ersten Speichern gibt es noch kein Bericht-DTO, deshalb der Rückgriff
   * auf den Einsatz.
   */
  get orderNumber(): string | null {
    /*
     * Bewusst || statt ??: liefert der Bericht einen leeren String (etwa weil er
     * gespeichert wurde, als am Auftrag noch keine Nummer hinterlegt war), muss
     * auf den Einsatz zurückgefallen werden. Mit ?? bliebe es bei '' und die
     * Nummer verschwände, obwohl sie am Auftrag steht.
     */
    return this.report?.orderNumber?.trim()
      || this.assignment.auftragOrderNumber?.trim()
      || null;
  }

  /**
   * Beschriftung zur Nummer – immer aus der tatsächlich angezeigten Nummer
   * abgeleitet, nie aus report.referenzLabel: sonst stünde dort "SES / IND",
   * wenn die Nummer aus dem Einsatz statt aus dem Bericht stammt.
   * Gleiche Ableitung wie TakedaReferenz.Label serverseitig.
   */
  get referenzLabel(): string {
    const wert = this.orderNumber ?? '';
    if (/^\s*ses/i.test(wert)) { return 'SES'; }
    if (/^\s*ind/i.test(wert)) { return 'IND'; }
    return 'SES / IND';
  }

  /**
   * Zeigt die ursprünglich erfasste Referenz nur dann an, wenn sie von der
   * Projektnummer abweicht – damit bleiben historische Berichte lesbar, ohne die
   * neue Erfassung zu überfrachten.
   */
  abweichendeReferenz(b: BereichForm): string | null {
    const alt = b.referenzNummer?.trim();
    if (!alt) { return null; }
    if (alt === (this.orderNumber ?? '').trim()) { return null; }

    const typ = b.referenzTyp === 'Angebot' ? 'Angebot' : 'SES';
    return `${typ} ${alt}`;
  }

  /** Auswahl für "weitere Person": Roster + Kolonne, ohne bereits vorhandene Personen. */
  availablePeople(b: BereichForm): ZeitPerson[] {
    const present = new Set(b.personen.map(p => p.key));
    const seen = new Set<string>();
    const result: ZeitPerson[] = [];
    for (const p of [...(this.data.plannedPeople ?? []), ...(this.data.rosterPeople ?? [])]) {
      const key = this.personKey(p);
      if (present.has(key) || seen.has(key)) { continue; }
      seen.add(key);
      result.push(p);
    }
    return result.sort((a, c) => a.label.localeCompare(c.label, 'de'));
  }

  personOptionKey(p: ZeitPerson): string {
    return this.personKey(p);
  }

  // ── Laden ──

  private load(): void {
    this.loading = true;
    this.loadError = null;
    this.showErrors = false;
    this.stammkundeService.getTakedaTagesbericht(this.assignmentAuftragId, this.assignment.id, this.workDate)
      .subscribe({
        next: report => {
          this.applyReport(report);
          this.loading = false;
        },
        error: err => {
          this.loading = false;
          this.report = null;
          this.bereiche.forEach(b => this.detachLvSearch(b));
          this.bereiche = [];
          this.loadError = this.errorMessage(err, 'Der Tagesbericht konnte nicht geladen werden.');
          this.snackBar.open(this.loadError, 'OK', { duration: 5000 });
        }
      });
  }

  private applyReport(report: TakedaTagesbericht | null): void {
    // Suchströme der bisherigen Bereiche beenden, bevor sie ersetzt werden.
    this.bereiche.forEach(b => this.detachLvSearch(b));

    this.report = report;
    this.status = report?.status ?? 'Draft';
    this.bereiche = report
      ? (report.arbeitsbereiche ?? []).map(b => this.fromDto(b))
      : [this.newBereich()]; // noch kein Bericht: ein Arbeitsbereich mit der Kolonne vorbelegen
    this.cleanSnapshot = report ? this.snapshot() : '';
  }

  async onDateChange(): Promise<void> {
    if (!this.workDate) { return; }
    if (this.isDirty && !(await this.confirm(
      'Ungespeicherte Änderungen',
      'Beim Wechsel des Tages gehen die ungespeicherten Änderungen verloren. Fortfahren?'))) {
      this.workDate = this.report ? this.toDateInput(new Date(this.report.workDate)) : this.data.workDate;
      return;
    }
    this.load();
  }

  // ── Arbeitsbereiche / Personen ──

  addBereich(): void {
    if (this.readOnly) { return; }
    const previous = this.bereiche[this.bereiche.length - 1];
    const bereich = this.newBereich();
    // Ort/Zuordnung vom vorherigen Bereich übernehmen – spart Tipparbeit.
    if (previous) {
      bereich.referenzTyp = previous.referenzTyp;
      bereich.ort = previous.ort;
    }
    this.bereiche.push(bereich);
  }

  async removeBereich(b: BereichForm): Promise<void> {
    if (this.readOnly) { return; }
    const hasContent = !!(
      b.referenzNummer.trim() ||
      b.beschreibung.trim() ||
      b.materialText.trim() ||
      this.bereichHours(b) > 0 ||
      b.lvPositionen.length > 0 ||
      b.freieMaterialien.length > 0
    );
    if (hasContent && !(await this.confirm(
      'Arbeitsbereich löschen',
      `Arbeitsbereich ${this.bereiche.indexOf(b) + 1} mit allen Angaben entfernen?`))) {
      return;
    }
    // Erst den Suchstrom beenden, dann den Bereich verwerfen.
    this.detachLvSearch(b);
    this.bereiche = this.bereiche.filter(x => x !== b);
  }

  addPerson(b: BereichForm): void {
    if (this.readOnly || !b.addKey) { return; }
    const person = this.availablePeople(b).find(p => this.personKey(p) === b.addKey);
    if (person) {
      b.personen.push(this.toRow(person, 0));
    }
    b.addKey = '';
  }

  removePerson(b: BereichForm, row: PersonRow): void {
    if (this.readOnly) { return; }
    b.personen = b.personen.filter(p => p !== row);
  }

  private loadActiveLv(): void {
    const customerId = this.assignment.customerId;

    if (!customerId) {
      this.activeLv = null;
      return;
    }

    this.activeLvLoading = true;

    this.stammkundeService
      .getTakedaAktivesLeistungsverzeichnis(customerId)
      .subscribe({
        next: lv => {
          this.activeLv = lv ?? null;
          this.activeLvLoading = false;
        },
        error: () => {
          this.activeLv = null;
          this.activeLvLoading = false;
        }
      });
  }

  // ── LV-Live-Suche ──

  /** Jede Eingabe im Suchfeld eines Arbeitsbereichs landet hier. */
  onLvSearchInput(b: BereichForm, value: string): void {
    b.lvSearch = value;

    if (this.readOnly) {
      return;
    }

    b.lvSearch$.next(value);
  }

  clearLvSearch(b: BereichForm): void {
    b.lvSearch = '';
    b.lvSearchResults = [];
    b.lvSearchTouched = false;
    b.lvSearchLoading = false;
    b.lvSearch$.next('');
  }

  /**
   * Baut die Live-Suche eines Arbeitsbereichs auf: 300 ms Debounce, ab 2 Zeichen,
   * laufende Anfragen werden bei neuer Eingabe per switchMap verworfen. Jeder
   * Arbeitsbereich hat seinen eigenen Strom, damit sich parallele Suchen nicht
   * gegenseitig überschreiben.
   */
  private attachLvSearch(b: BereichForm): BereichForm {
    b.lvSearchSub = b.lvSearch$
      .pipe(
        debounceTime(this.lvSearchDebounceMs),
        map(value => (value ?? '').trim()),
        distinctUntilChanged(),
        switchMap(term => {
          if (term.length < this.minLvSearchLength) {
            // Unter 2 Zeichen: Liste leeren, nicht suchen.
            b.lvSearchLoading = false;
            b.lvSearchTouched = false;
            return of<TakedaTagesberichtLvPosition[]>([]);
          }

          b.lvSearchLoading = true;
          b.lvSearchTouched = true;

          return this.stammkundeService
            .searchTakedaTagesberichtLvPositionen(
              this.assignmentAuftragId,
              this.assignment.id,
              term
            )
            .pipe(
              catchError(err => {
                this.snackBar.open(
                  this.errorMessage(
                    err,
                    'Die LV-Positionen konnten nicht geladen werden.'
                  ),
                  'OK',
                  { duration: 5000 }
                );
                return of<TakedaTagesberichtLvPosition[]>([]);
              })
            );
        })
      )
      .subscribe(results => {
        b.lvSearchResults = results ?? [];
        b.lvSearchLoading = false;
      });

    return b;
  }

  /** Beendet den Suchstrom eines Arbeitsbereichs (kein Leak beim Entfernen). */
  private detachLvSearch(b: BereichForm): void {
    b.lvSearchSub?.unsubscribe();
    b.lvSearchSub = null;
    b.lvSearch$.complete();
  }

  /** Haupttext eines Treffers: Bezeichnung bevorzugt, sonst Stichwort. */
  lvHauptText(position: { bezeichnung?: string | null; stichwort?: string | null; nummer?: string }): string {
    return (position.bezeichnung?.trim() || position.stichwort?.trim() || position.nummer || '').trim();
  }

  /** Zusatzzeile: nur zeigen, wenn sie nicht schon der Haupttext ist. */
  lvZusatzText(position: { bezeichnung?: string | null; stichwort?: string | null }): string | null {
    const stichwort = position.stichwort?.trim();
    if (!stichwort) { return null; }
    return position.bezeichnung?.trim() ? stichwort : null;
  }

  addLvPosition(
    b: BereichForm,
    position: TakedaTagesberichtLvPosition
  ): void {
    if (this.readOnly) {
      return;
    }

    const alreadyExists = b.lvPositionen.some(
      x => x.lvPositionId === position.id
    );

    if (alreadyExists) {
      this.snackBar.open(
        'Diese LV-Position wurde in diesem Arbeitsbereich bereits hinzugefügt.',
        'OK',
        { duration: 3000 }
      );
      return;
    }

    b.lvPositionen.push({
      id: null,
      lvPositionId: position.id,

      nummer: position.nummer,
      stichwort: position.stichwort,
      bezeichnung: position.bezeichnung,
      typ: position.typ,
      einheit: position.einheit,

      menge: 1
    });

    this.clearLvSearch(b);
  }

  removeLvPosition(
    b: BereichForm,
    position: LvRow
  ): void {
    if (this.readOnly) {
      return;
    }

    b.lvPositionen = b.lvPositionen.filter(
      p => p !== position
    );
  }

  // ── Geplantes Material (SOLL) ──
  //
  // Die Planung hängt am Einsatz, nicht am Tagesbericht: sie ist eine Vorgabe und
  // KEIN erfasstes Material. Sie wird hier nur angezeigt und auf Wunsch in das
  // tatsächliche Material übernommen. Die Planzeile selbst bleibt dabei
  // unverändert – Planmenge und Ist-Menge sind zwei getrennte Werte.

  /** Für diesen Einsatz geplante LV-Positionen. */
  get geplantesMaterial(): TakedaEinsatzPlanLvPosition[] {
    return this.assignment?.planLvPositionen ?? [];
  }

  /** Geplantes freies Material (ohne LV-Nummer) statt einer LV-Position? */
  istFreiePlanung(plan: TakedaEinsatzPlanLvPosition): boolean {
    return plan.art === 'Frei';
  }

  /**
   * Nur Positionen MIT Planmenge lassen sich übernehmen – LV-Positionen mit
   * LV-Referenz und freies Material mit Bezeichnung.
   */
  get uebernehmbarePlanmengen(): TakedaEinsatzPlanLvPosition[] {
    return this.geplantesMaterial.filter(p =>
      (p.planMenge ?? 0) > 0
      && (this.istFreiePlanung(p) ? !!p.bezeichnung?.trim() : p.lvPositionId != null));
  }

  /**
   * Ist diese Planposition im Arbeitsbereich schon als Ist-Menge vorhanden?
   * LV-Positionen an ihrer LV-Referenz, freies Material an Bezeichnung + Einheit
   * (ohne Groß-/Kleinschreibung) – die Ist-Menge darf dabei abweichen.
   * Steuert die Anzeige und verhindert doppelte Übernahmen.
   */
  planPositionUebernommen(b: BereichForm, plan: TakedaEinsatzPlanLvPosition): boolean {
    if (this.istFreiePlanung(plan)) {
      const schluessel = this.freiSchluessel(plan.bezeichnung, plan.einheit);
      return b.freieMaterialien.some(m => this.freiSchluessel(m.bezeichnung, m.einheit) === schluessel);
    }
    return b.lvPositionen.some(x => x.lvPositionId === plan.lvPositionId);
  }

  private freiSchluessel(bezeichnung?: string | null, einheit?: string | null): string {
    return `${(bezeichnung ?? '').trim().toLocaleLowerCase('de')}|${(einheit ?? '').trim().toLocaleLowerCase('de')}`;
  }

  planLabel(plan: TakedaEinsatzPlanLvPosition): string {
    return plan.bezeichnung?.trim() || plan.stichwort?.trim() || plan.nummer;
  }

  /**
   * Übernimmt die Planmengen als Ist-Mengen in diesen Arbeitsbereich.
   *
   * Erzeugt bewusst keine Duplikate: eine LV-Position, die hier schon erfasst ist,
   * wird übersprungen – ihre Menge bleibt unangetastet, auch wenn sie von der
   * Planmenge abweicht. Die Aktion ist damit beliebig oft aufrufbar.
   *
   * Nach der Übernahme sind die Mengen normale Eingabefelder und frei änderbar;
   * zusätzliches freies oder LV-Material lässt sich weiter wie bisher ergänzen.
   */
  planmengenUebernehmen(b: BereichForm): void {
    if (this.readOnly) {
      return;
    }

    let uebernommen = 0;
    let uebersprungen = 0;

    for (const plan of this.uebernehmbarePlanmengen) {
      if (this.planPositionUebernommen(b, plan)) {
        uebersprungen++;
        continue;
      }

      // Freies Material wird zur ganz normalen freien Ist-Zeile; die Planzeile
      // bleibt unverändert, eine spätere Mengenkorrektur ändert die Planung nicht.
      if (this.istFreiePlanung(plan)) {
        b.freieMaterialien.push({
          id: null,
          bezeichnung: plan.bezeichnung!.trim().slice(0, this.maxFreiesMaterialBezeichnungLength),
          menge: plan.planMenge ?? null,
          einheit: plan.einheit?.trim() ?? ''
        });
        uebernommen++;
        continue;
      }

      b.lvPositionen.push({
        id: null,
        lvPositionId: plan.lvPositionId,
        nummer: plan.nummer,
        stichwort: plan.stichwort,
        bezeichnung: plan.bezeichnung,
        typ: null,
        einheit: plan.einheit,
        menge: plan.planMenge ?? null
      });

      uebernommen++;
    }

    if (uebernommen === 0) {
      this.snackBar.open(
        uebersprungen > 0
          ? 'Alle geplanten Positionen sind hier schon erfasst.'
          : 'Es gibt keine Planmengen zum Übernehmen.',
        'OK', { duration: 4000 });
      return;
    }

    const zusatz = uebersprungen > 0
      ? ` ${uebersprungen} war${uebersprungen === 1 ? '' : 'en'} schon erfasst.`
      : '';

    this.snackBar.open(
      `${uebernommen} Position${uebernommen === 1 ? '' : 'en'} übernommen.${zusatz} `
      + 'Mengen jetzt bei Bedarf anpassen.',
      'OK', { duration: 6000 });
  }

  // ── Freies Material ──

  addFreiesMaterial(b: BereichForm): void {
    if (this.readOnly) {
      return;
    }

    b.freieMaterialien.push({
      id: null,
      bezeichnung: '',
      menge: 1,
      einheit: ''
    });
  }

  removeFreiesMaterial(b: BereichForm, row: FreiesMaterialRow): void {
    if (this.readOnly) {
      return;
    }

    b.freieMaterialien = b.freieMaterialien.filter(m => m !== row);
  }

  // ── Validierung ──

  /** Pflichtangaben für das Abschließen (Entwürfe dürfen unvollständig sein). */
  bereichErrors(b: BereichForm): string[] {
    const errors: string[] = [];
    // Die SES-/IND-Nummer wird hier nicht mehr erfasst – fehlt sie, liegt das am
    // Projekt und wird unterhalb des Berichts einmal gemeldet, nicht je Bereich.
    if (!b.beschreibung.trim()) { errors.push('Beschreibung der ausgeführten Arbeiten fehlt'); }
    if (!b.personen.some(p => (Number(p.hours) || 0) > 0)) { errors.push('mindestens eine Person mit Stunden > 0'); }
    return errors;
  }

  private submitErrors(): string[] {
    if (this.bereiche.length === 0) {
      return ['Bitte mindestens einen Arbeitsbereich erfassen.'];
    }
    const errors: string[] = [];
    this.bereiche.forEach((b, i) => {
      const list = this.bereichErrors(b);
      if (list.length) { errors.push(`Arbeitsbereich ${i + 1}: ${list.join(', ')}.`); }
    });
    return errors;
  }

  /** Grundprüfung, die auch für Entwürfe gilt (sonst lehnt das Backend ab). */
  private draftError(): string | null {
    for (const b of this.bereiche) {

      for (const p of b.personen) {
        const h = Number(p.hours) || 0;

        if (h < 0 || h > 24) {
          return `Stunden für ${p.label} müssen zwischen 0 und 24 liegen.`;
        }
      }

      for (const lv of b.lvPositionen) {
        const menge = Number(lv.menge);

        if (!Number.isFinite(menge) || menge <= 0) {
          return `Die Menge der LV-Position ${lv.nummer} muss größer als 0 sein.`;
        }
      }

      for (const material of b.freieMaterialien) {
        const bezeichnung = material.bezeichnung?.trim() ?? '';

        if (!bezeichnung) {
          return 'Bitte für jedes freie Material eine Bezeichnung angeben.';
        }

        if (bezeichnung.length > this.maxFreiesMaterialBezeichnungLength) {
          return `Die Bezeichnung „${bezeichnung.slice(0, 30)}…" ist zu lang `
            + `(max. ${this.maxFreiesMaterialBezeichnungLength} Zeichen).`;
        }

        const menge = Number(material.menge);

        if (!Number.isFinite(menge) || menge <= 0) {
          return `Die Menge von „${bezeichnung}" muss größer als 0 sein.`;
        }
      }

    }

    return null;
  }

  // ── Speichern / Abschließen ──

  async saveDraft(): Promise<void> {
    if (this.readOnly || this.saving) { return; }
    if (!this.ensureOnline()) { return; }
    const draftError = this.draftError();
    if (draftError) {
      this.snackBar.open(draftError, 'OK', { duration: 5000 });
      return;
    }

    this.saving = true;
    try {
      await this.persistDraft();
      this.snackBar.open('Entwurf gespeichert.', 'OK', { duration: 3000 });
    } catch (err) {
      this.snackBar.open(this.errorMessage(err, 'Der Entwurf konnte nicht gespeichert werden.'), 'OK', { duration: 5000 });
    } finally {
      this.saving = false;
    }
  }

  async submit(): Promise<void> {
    if (this.readOnly || this.saving) { return; }
    if (!this.ensureOnline()) { return; }

    this.showErrors = true;
    const draftError = this.draftError();
    const errors = draftError ? [draftError] : this.submitErrors();
    if (errors.length) {
      this.snackBar.open(errors[0], 'OK', { duration: 6000 });
      return;
    }

    const confirmed = await this.confirm(
      'Tagesbericht abschließen',
      `Tagesbericht mit ${this.formatHours(this.totalHours)} h abschließen? Die Stunden werden in die ` +
      'Stundenübersicht übernommen. Danach kann der Bericht nicht mehr bearbeitet werden.');
    if (!confirmed) { return; }

    this.saving = true;
    try {
      // Aktuellen Stand zuerst speichern, dann abschließen.
      await this.persistDraft();
      const result = await firstValueFrom(this.stammkundeService.submitTakedaTagesbericht(
        this.assignmentAuftragId, this.assignment.id, this.workDate));
      this.applyReport(result);
      this.submitted = true;
      this.showErrors = false;
      this.snackBar.open('Tagesbericht abgeschlossen – Stunden wurden übernommen.', 'OK', { duration: 4000 });
    } catch (err) {
      this.snackBar.open(this.errorMessage(err, 'Der Tagesbericht konnte nicht abgeschlossen werden.'), 'OK',
        { duration: 6000 });
    } finally {
      this.saving = false;
    }
  }

  private async persistDraft(): Promise<void> {
    const saved = await firstValueFrom(this.stammkundeService.saveTakedaTagesbericht(
      this.assignmentAuftragId, this.assignment.id, this.buildPayload()));
    this.applyReport(saved);
    this.savedOnce = true;
  }

  async close(): Promise<void> {
    if (this.saving) { return; }
    if (this.isDirty && !(await this.confirm(
      'Ungespeicherte Änderungen',
      'Der Tagesbericht enthält ungespeicherte Änderungen. Trotzdem schließen?'))) {
      return;
    }
    this.dialogRef.close({ saved: this.savedOnce, submitted: this.submitted });
  }

  // ── Helpers ──

  private get assignmentAuftragId(): number {
    return this.assignment.auftragId ?? 0;
  }

  private get isDirty(): boolean {
    if (this.readOnly || this.loading) { return false; }
    // Neuer, noch nie gespeicherter Bericht: nur "dirty", wenn etwas eingetragen wurde.
    if (!this.report) {
      return this.bereiche.some(b =>
        !!(
          b.referenzNummer.trim() ||
          b.ort.trim() ||
          b.beschreibung.trim() ||
          b.materialText.trim() ||
          this.bereichHours(b) > 0 ||
          b.lvPositionen.length > 0 ||
          b.freieMaterialien.length > 0
        )
      ) || this.bereiche.length !== 1;
    }
    return this.snapshot() !== this.cleanSnapshot;
  }

  private snapshot(): string {
    return JSON.stringify(this.buildPayload());
  }

  private ensureOnline(): boolean {
    if (this.isOffline) {
      this.snackBar.open('Der Tagesbericht braucht eine Verbindung. Bitte später erneut versuchen.', 'OK',
        { duration: 5000 });
      return false;
    }
    return true;
  }

  private buildPayload(): TakedaTagesberichtSave {
    return {
      workDate: `${this.workDate}T00:00:00`,

      arbeitsbereiche: this.bereiche.map(b => ({
        referenzTyp: b.referenzTyp,

        referenzNummer:
          b.referenzNummer.trim(),

        ort:
          b.ort.trim() || null,

        beschreibung:
          b.beschreibung.trim(),

        materialText:
          b.materialText.trim() || null,

        lvPositionen: b.lvPositionen
          .filter(p => p.lvPositionId != null)
          .map(p => ({
            lvPositionId: p.lvPositionId!,
            menge: Number(p.menge) || 0
          })),

        /*
         * Freies Material: bestehende Zeilen gehen MIT ihrer id zurück, damit das
         * Backend einen dort bereits gepflegten Preis übernehmen kann. Ein Preis
         * wird bewusst nie gesendet – er lässt sich über den Tagesbericht also
         * auch mit einem manipulierten Request nicht setzen.
         */
        freieMaterialien: b.freieMaterialien
          .filter(m => !!m.bezeichnung?.trim())
          .map(m => ({
            id: m.id ?? null,
            bezeichnung: m.bezeichnung.trim(),
            menge: Number(m.menge) || 0,
            einheit: m.einheit?.trim() || null
          })),

        arbeitszeiten: b.personen.map(p => ({
          personUserId:
            p.workerId == null
              ? (p.personUserId ?? null)
              : null,

          workerId:
            p.workerId ?? null,

          personName:
            p.workerId == null && !p.personUserId
              ? (p.personName ?? p.label)
              : null,

          hours:
            Number(p.hours) || 0
        }))
      }))
    };
  }

  private newBereich(): BereichForm {
    return this.attachLvSearch({
      referenzTyp: 'Ses',
      referenzNummer: '',
      ort: '',
      beschreibung: '',
      materialText: '',

      personen: (this.data.plannedPeople ?? [])
        .map(p => this.toRow(p, 0)),

      lvPositionen: [],
      freieMaterialien: [],

      lvSearch: '',
      lvSearch$: new Subject<string>(),
      lvSearchSub: null,
      lvSearchResults: [],
      lvSearchLoading: false,
      lvSearchTouched: false,

      addKey: ''
    });
  }

  private fromDto(b: TakedaArbeitsbereich): BereichForm {
    return this.attachLvSearch({
      referenzTyp:
        b.referenzTyp === 'Angebot'
          ? 'Angebot'
          : 'Ses',

      referenzNummer: b.referenzNummer ?? '',
      ort: b.ort ?? '',
      beschreibung: b.beschreibung ?? '',
      materialText: b.materialText ?? '',

      personen: (b.arbeitszeiten ?? []).map(z => ({
        key: this.personKey({
          label: z.personName,
          workerId: z.workerId,
          personUserId: z.personUserId
        }),
        label: z.personName,
        personUserId: z.personUserId,
        workerId: z.workerId,
        personName:
          z.workerId == null && !z.personUserId
            ? z.personName
            : null,
        hours: z.hours
      })),

      lvPositionen: (b.lvPositionen ?? []).map(p => ({
        id: p.id,
        lvPositionId: p.lvPositionId,

        nummer: p.nummer,
        stichwort: p.stichwort,
        bezeichnung: p.bezeichnung,
        typ: p.typ,
        einheit: p.einheit,

        menge: p.menge
      })),

      // Die id wird mitgeführt, damit ein serverseitig bereits gepflegter Preis
      // beim nächsten Speichern erhalten bleibt.
      freieMaterialien: (b.freieMaterialien ?? []).map(m => ({
        id: m.id,
        bezeichnung: m.bezeichnung ?? '',
        menge: m.menge,
        einheit: m.einheit ?? ''
      })),

      lvSearch: '',
      lvSearch$: new Subject<string>(),
      lvSearchSub: null,
      lvSearchResults: [],
      lvSearchLoading: false,
      lvSearchTouched: false,

      addKey: ''
    });
  }

  private toRow(p: ZeitPerson, hours: number): PersonRow {
    return {
      key: this.personKey(p),
      label: p.label,
      personUserId: p.personUserId,
      workerId: p.workerId,
      personName: p.workerId == null && !p.personUserId ? p.label : null,
      hours
    };
  }

  private personKey(p: ZeitPerson): string {
    return p.workerId != null ? `w:${p.workerId}`
      : p.personUserId ? `u:${p.personUserId}`
        : `n:${p.label}`;
  }

  private confirm(title: string, message: string): Promise<boolean> {
    const ref = this.dialog.open(ConfirmDialogComponent, { width: '420px', maxWidth: '92vw', data: { title, message } });
    return firstValueFrom(ref.afterClosed()).then(r => !!r);
  }

  private errorMessage(err: unknown, fallback: string): string {
    const http = err as HttpErrorResponse;
    const message = http?.error?.message;
    if (typeof message === 'string' && message) { return message; }
    switch (http?.status) {
      case 0: return 'Keine Verbindung zum Server.';
      case 403: return 'Keine Berechtigung für diesen Tagesbericht.';
      case 404: return 'Einsatz oder Tagesbericht nicht gefunden.';
      default: return fallback;
    }
  }

  formatHours(hours: number): string {
    return hours.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 2 });
  }

  trackByIndex(index: number): number {
    return index;
  }

  trackByPerson(_: number, row: PersonRow): string {
    return row.key;
  }

  private toDateInput(date: Date): string {
    const y = date.getFullYear();
    const m = `${date.getMonth() + 1}`.padStart(2, '0');
    const d = `${date.getDate()}`.padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  /**
   * Gegenstück zu toDateInput: 'yyyy-MM-dd' als LOKALES Datum.
   *
   * Bewusst komponentenweise statt new Date('yyyy-MM-dd') – letzteres wird als
   * UTC-Mitternacht gelesen und läge östlich von Greenwich einen Tag daneben.
   */
  private vonDateInput(value: string | null | undefined): Date | null {
    if (!value) { return null; }

    const teile = value.split('-').map(Number);
    if (teile.length !== 3 || teile.some(n => !Number.isFinite(n))) { return null; }

    const [jahr, monat, tag] = teile;
    return new Date(jahr, monat - 1, tag);
  }
}
